import { ApplicationRef, Injectable, Type, inject } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from '@openng/optimus-ui/dynamicdialog';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { FormDialogComponent } from '../components/form-dialog/form-dialog.component';
import { MessageDialogComponent } from '../components/message-dialog/message-dialog.component';
import { WaitingFormComponent, WaitingFormStatus } from '../components/waiting-form/waiting-form.component';
import { ButtonDefinition } from '../models/button-definition.model';
import { MessageDialogOptions } from '../models/message-dialog-options.model';
import { FrontendField } from '../netgen/FrontendField';
import { FrontendFieldType } from '../netgen/FrontendFieldType';
import { FrontendForm } from '../netgen/FrontendForm';
import { ArrayOrSingle } from '../shared/array-or-single.model';
import { ContainerDialogComponent } from '../components/container-dialog/container-dialog.component';

@Injectable()
export class MessageDialogService {

    dialogService = inject(DialogService);
    applicationRef = inject(ApplicationRef);

    private activeComponentDialogs = new Map<Type<unknown>, Promise<OrdinaryObject>>();

    ok = new ButtonDefinition("ok", "OK", true, "");
    yes = new ButtonDefinition("yes", "Ja", true, "");
    no = new ButtonDefinition("no", "Nein", false, "");
    cancel = new ButtonDefinition("cancel", "Abbrechen", false, "");
    abort = new ButtonDefinition("abort", "Abbrechen", false, "");
    retry = new ButtonDefinition("retry", "Wiederholen", true, "");
    ignore = new ButtonDefinition("ignore", "Ignorieren", false, "");
    confirm = new ButtonDefinition("confirm", "Bestätigen", true, "");
    save = new ButtonDefinition("save", "Speichern", true, "");
    delete = new ButtonDefinition("delete", "Löschen", false, "");

    okCancel = [this.ok, this.cancel];
    yesNoCancel = [this.yes, this.no, this.cancel];
    abortRetryIgnore = [this.abort, this.retry, this.ignore];
    retryCancel = [this.retry, this.cancel];

    translateButtons(buttons: ArrayOrSingle<ButtonDefinition>): ButtonDefinition[] {
        let result = Objects.cloneDeep(Arrays.as(buttons));

        for (let button of result) {
            button.caption = MainAppService.instance.t("general.buttons." + button.id, null, button.caption);
        }

        return result;
    }

    private openDialog<T>(component: Type<T>, params: DynamicDialogConfig): DynamicDialogRef<T> {
        let ref = this.dialogService.open(component, params);
        if (ref == null) {
            throw new Error("Der Dialog '" + (params.header ?? component.name) + "' konnte nicht geöffnet werden, weil bereits ein Dialog dieses Typs geöffnet ist. Komponente: " + component.name);
        }
        return ref;
    }

    infoNoWait(message: string, title?: string): void {
        this.showNoWait(message, Strings.def(title, "Information"), this.ok);
    }

    showNoWait(message: string, caption: string, buttons: ArrayOrSingle<ButtonDefinition>, _icon = "", monospace?: boolean): DynamicDialogRef {
        return this.openDialog(MessageDialogComponent, {
            data: {
                message,
                buttons: this.translateButtons(buttons)
            },
            modal: true,
            header: caption,
            // width: '70%',
            contentStyle: { "max-height": "350px", overflow: "auto", "font-family": monospace ? "monospace" : null }
        });
    }

    formNoWait(form: FrontendForm, caption: string, buttons: ArrayOrSingle<ButtonDefinition>, options: MessageDialogOptions = null): DynamicDialogRef {
        return this.openDialog(FormDialogComponent, {
            data: {
                form,
                buttons: this.translateButtons(buttons)
            },
            modal: true,
            focusOnShow: false,
            header: caption,
            width: options?.width ?? "600px",
            contentStyle: { width: "100%", "max-height": "90vh", overflow: "auto" }
        });
    }

    async info(message: string, title?: string, options?: { icon?: string; monospace?: boolean; details?: string; detailsLabel?: string; detailsInline?: boolean }): Promise<void> {
        await this.show(message, Strings.def(title, "Information"), this.ok, options);
    }

    async yesNo(message: string, title?: string, closeOption = "no", options?: { icon?: string; monospace?: boolean }): Promise<boolean> {
        return await this.show(message, title, [this.yes, this.no], { closeOption, icon: options?.icon, monospace: options?.monospace }) == "yes";
    }

    async show(message: string, caption: string, buttons: ArrayOrSingle<ButtonDefinition>, options?: { icon?: string; monospace?: boolean; closeOption?: string; details?: string; detailsLabel?: string; detailsInline?: boolean }): Promise<any> {
        let params = {
            data: {
                message,
                details: options?.details,
                detailsLabel: options?.detailsLabel,
                detailsInline: options?.detailsInline,
                buttons: this.translateButtons(buttons)
            },
            header: caption,
            modal: true,
            closable: !Utils.isNoe(options?.closeOption),
            // width: '70%',
            contentStyle: { "max-height": "350px", overflow: "auto", "font-family": options?.monospace ? "monospace" : null }
        };
        let ref = this.openDialog(MessageDialogComponent, params);
        return new Promise((resolve, _) => {
            this.applicationRef.tick();
            let result: any[] = [];
            //Es geht hier erst weiter wenn der Dialog destroyed wurde, ansonsten kann es sein, dass zwischen close und destroy
            //ein neuer Dialog geöffnet wird. Dann bleibt die GUI hängen.
            ref.onClose.subscribe(r => result.push(r ?? options?.closeOption));
            ref.onDestroy.subscribe(__ => resolve(result[0]));
        });
    }

    textArea(caption: string, text: string, buttons?: ArrayOrSingle<ButtonDefinition>, monospaced?: boolean, height?: number, options: Partial<MessageDialogOptions> = null, developerText = false): DynamicDialogRef {
        let maximumHeight = developerText ? "calc(100dvh - 240px)" : "calc(100dvh - 205px)";
        let textField = new FrontendField("text", "", FrontendFieldType.textArea, {
            hasLabel: false,
            value: text,
            monospaced,
            textAreaWordWrap: !developerText,
            fixedHeight: "min(" + (height != null ? height + "px" : "80vh") + ", " + maximumHeight + ")"
        });
        let wordWrapField = developerText ? new FrontendField("automaticWordWrap", "Automatischer Zeilenumbruch", FrontendFieldType.bool, {
            value: false,
            onValueChanged: event => {
                textField.textAreaWordWrap = Utils.toBool(event.newValue);
            }
        }) : null;
        return this.formNoWait(new FrontendForm([wordWrapField, textField]), caption, buttons ?? this.ok, Objects.with(Objects.fromPlain(MessageDialogOptions, options ?? {}), "width", "90%"));
    }

    async form(form: FrontendForm, caption: string, buttons?: ArrayOrSingle<ButtonDefinition>, closable?: boolean, fullScreen?: boolean, dialogWidth?: string): Promise<string> {
        let params = {
            data: {
                form,
                buttons: this.translateButtons(buttons ?? this.okCancel)
            },
            header: caption,
            modal: true,
            focusOnShow: false,
            closable,
            width: dialogWidth ?? (fullScreen ? "100vw" : "600px"),
            height: fullScreen ? '100vh' : 'auto',
            contentStyle: fullScreen 
                ? { "overflow": "auto" } 
                : { "max-width": "600px", "max-height": "90vh", overflow: "auto" } 
        };
        let ref = this.openDialog(FormDialogComponent, params);
        return new Promise((resolve, _) => {
            let result: any[] = [];
            //Es geht hier erst weiter wenn der Dialog destroyed wurde, ansonsten kann es sein, dass zwischen close und destroy
            //ein neuer Dialog geöffnet wird. Dann bleibt die GUI hängen.
            ref.onClose.subscribe(r => {
                result.push(r);
            });
            ref.onDestroy.subscribe(__ => {
                resolve(result[0] as string ?? "cancel");
            });
        });
    }

    async component<T>(component: Type<T>, caption: string, options: Partial<MessageDialogOptions> = null, inputs?: Partial<T>): Promise<OrdinaryObject> {
        let activeDialog = this.activeComponentDialogs.get(component);
        if (activeDialog != null) {
            await activeDialog;
            return {};
        }

        let outputs: OrdinaryObject = {};
        let ref = this.openDialog(ContainerDialogComponent, {
            data: {
                component,
                inputs,
                outputs
            },
            modal: true,
            focusOnShow: false,
            closable: options?.closable ?? true,
            header: caption,
            width: options?.width ?? '600px',
            contentStyle: { width: '100%', 'max-height': options?.maxHeight ?? "90vh", height: options?.height ?? "90vh", overflow: 'auto' }
        });
        let dialog = new Promise<OrdinaryObject>((resolve, _) => {
            //Es geht hier erst weiter wenn der Dialog destroyed wurde, ansonsten kann es sein, dass zwischen close und destroy
            //ein neuer Dialog geöffnet wird. Dann bleibt die GUI hängen.
            ref.onDestroy.subscribe(__ => {
                resolve(outputs);
            });
        });
        this.activeComponentDialogs.set(component, dialog);
        try {
            return await dialog;
        }
        finally {
            if (this.activeComponentDialogs.get(component) == dialog) {
                this.activeComponentDialogs.delete(component);
            }
        }
    }

    async input<T>(field: FrontendField, title: string, fullScreen?: boolean): Promise<T> {
        let form = new FrontendForm([field]);
        return await this.form(form, title, null, false, fullScreen) != "ok" ? null : form.getValue(field.id) as T;
    }

    async formGetObject(form: FrontendForm, caption: string, closable?: boolean): Promise<OrdinaryObject> {
        let ref = this.openDialog(FormDialogComponent, {
            data: {
                form,
                buttons: this.translateButtons(this.okCancel)
            },
            modal: true,
            focusOnShow: false,
            header: caption,
            closable,
            contentStyle: { "max-width": "600px", "max-height": "90vh", overflow: "auto" }
        });
        return new Promise((resolve, _) => {
          //  this.applicationRef.tick(); Zum testen rausgenommen wegen Endlosschleife
            let result: any[] = [];
            //Es geht hier erst weiter wenn der Dialog destroyed wurde, ansonsten kann es sein, dass zwischen close und destroy
            //ein neuer Dialog geöffnet wird. Dann bleibt die GUI hängen.
            ref.onClose.subscribe(r => {
                result.push(r);
            });
            ref.onDestroy.subscribe(__ => {
                resolve((result[0] as string) == "ok" ? form.getAsObject() : null);
            });
        });
    }

    waitingForm(caption: string, message: string, progressUrl: string, callback: () => WaitingFormStatus): DynamicDialogRef {
        return this.openDialog(WaitingFormComponent, {
            data: {
                message,
                progressUrl,
                callback
            },
            header: caption,
            modal: true,
            closable: false,
            focusOnShow: false,
            contentStyle: { "max-height": "350px", overflow: "auto" }
        });
    }
}
